import { formatDateValue } from "@common";

export const EMPTY_VALUE = "";

export const formatArrayValue = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return EMPTY_VALUE;
  return (
    arr
      .map((item) => {
        if (item === null || item === undefined) return "";
        if (typeof item === "object") {
          return (
            item?.productName ||
            item?.serviceName ||
            item?.jobTitle ||
            item?.name ||
            item?.label ||
            item?.value ||
            JSON.stringify(item)
          );
        }
        return String(item).trim();
      })
      .filter(Boolean)
      .join(", ") || EMPTY_VALUE
  );
};

export const formatObjectValue = (obj) => {
  if (!obj || typeof obj !== "object") return String(obj ?? "");
  const val =
    obj?.sourceName ||
    obj?.source ||
    obj?.inquirySource ||
    obj?.prospectSource ||
    obj?.mediumName ||
    obj?.medium ||
    obj?.industryName ||
    obj?.domainName ||
    obj?.productName ||
    obj?.serviceName ||
    obj?.currencyName ||
    obj?.currency ||
    obj?.priorityType ||
    obj?.inquiryType ||
    obj?.status ||
    obj?.name ||
    obj?.label ||
    obj?.value ||
    obj?.inquiryName;

  if (typeof val === "object" && val !== null) {
    return formatObjectValue(val);
  }
  return val != null && val !== "" ? String(val).trim() : EMPTY_VALUE;
};

export const getValue = (value) => {
  if (value === null || value === undefined || value === "") return EMPTY_VALUE;

  if (Array.isArray(value)) {
    return formatArrayValue(value);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return formatArrayValue(parsed);
        }
      } catch {
        // Fall back
      }
    }
    return value;
  }

  if (typeof value === "object") {
    return formatObjectValue(value);
  }

  return String(value);
};
const getCurrecyValue = (currencyCode, currencyValue) => {
  return currencyValue ? `${currencyCode || ""} ${currencyValue || ""}` : ""
}
export const getBasicInformationView = (data = {}) => {
  return {
    left: [
      { label: "Subject", value: getValue(data?.subject) },
      { label: "Company Name", value: getValue(data?.companyName) },
      { label: "First Name", value: getValue(data?.firstName) },
      { label: "Last Name", value: getValue(data?.lastName) },
      { label: "Email", value: getValue(data?.email) },
      { label: "Contact Number", value: data?.phone ? `${data?.dialingCode || ""} ${data?.phone || ""}`.trim() : "" },
      { label: "Job Title", value: getValue(data?.jobTitle) },
    ],
    right: [
      { label: "Interested Products", value: getValue(data?.interestedProducts) },
      { label: "Interested Services", value: getValue(data?.interestedServices) },
      { label: "Estimated Budget", value: getCurrecyValue(data?.customerCurrency?.currencyCode, data?.estimatedBudget) },
    ],
    fullWidth: [
      { label: "Description", value: getValue(data?.description) },
      { label: "Note", value: getValue(data?.note) },
    ],
  };
};

export const getInquirySourceView = (data = {}) => ({
  left: [
    {
      label: "Inquiry Source",
      value: getValue(
        data?.inquirySource?.sourceName ||
        data?.inquirySource?.name ||
        data?.inquirySource
      ),
    },
    {
      label: "URL",
      value: getValue(data?.landingPageUrl),
    },
    {
      label: "Inquiry Medium",
      value: getValue(data?.inquiryMedium),
    },
  ],
  right: [],
  fullWidth: [],
});

export const getInquiryQualificationView = (data = {}) => ({
  left: [
    {
      label: "Expected Timeline",
      value: formatDateValue(data?.expectedTimeline),
    },
    {
      label: "Intent Score",
      value: getValue(data?.buyingIntentScore),
    },
    {
      label: "Intent Level",
      value: getValue(
        data?.buyingIntentLevel?.intentLevelName ||
        data?.buyingIntentLevel?.intentLevel ||
        data?.buyingIntentLevel?.name ||
        data?.buyingIntentLevel
      ),
    },
  ],
  right: [
    {
      label: "Qualification Readiness",
      value: getValue(
        data?.qualificationReadiness?.qualificationReadinessName ||
        data?.qualificationReadiness?.readiness ||
        data?.qualificationReadiness?.name ||
        data?.qualificationReadiness
      ),
    },
    {
      label: "Reason",
      value: getValue(data?.reason),
    },
    {
      label: "Qualification Disposition",
      value: getValue(data?.qualificationDisposition),
    },
  ],
  fullWidth: [
    {
      label: "Note",
      value: getValue(data?.note || ""),
    },
  ],
});

const resolveEntityName = (entity, keys = []) => {
  if (!entity) return "";
  if (typeof entity !== "object") return String(entity);
  for (const key of keys) {
    if (entity[key]) return entity[key];
  }
  return entity?.name || "";
};

const resolveContactName = (contact) => {
  if (!contact) return "";
  if (typeof contact !== "object") return String(contact);
  if (contact.fullName) return contact.fullName;
  if (contact.firstName) {
    return [contact.firstName, contact.lastName].filter(Boolean).join(" ");
  }
  return contact.contactName || contact.name || "";
};

const createClickHandler = (hasValue, handler, param) => {
  if (hasValue && handler) {
    return () => handler(param);
  }
  return undefined;
};

const hasDisplayValue = (val) => Boolean(val && val !== EMPTY_VALUE);

export const getInquiryTaggingView = (data = {}, handlers = {}) => {
  const campDisplay = getValue(
    resolveEntityName(data?.campaign, ["campaignName", "name", "title"])
  );
  const prsDisplay = getValue(
    resolveEntityName(data?.prospect, ["prospectName", "name", "companyName"])
  );
  const prsContactDisplay = getValue(resolveContactName(data?.prospectContact));
  const inqDisplay = getValue(
    resolveEntityName(data?.inquiry, ["inquiryName", "inquiryNo", "name"])
  );
  const accountDisplay = getValue(
    resolveEntityName(data?.customerAccount || data?.account, [
      "customerAccountName",
      "accountName",
      "name",
    ])
  );
  const contactDisplay = getValue(resolveContactName(data?.contact));

  const hasCampaign = hasDisplayValue(campDisplay);
  const hasProspect = hasDisplayValue(prsDisplay);
  const hasProspectContact = hasDisplayValue(prsContactDisplay);
  const hasInquiry = hasDisplayValue(inqDisplay);

  const fullWidth = [
    {
      label: "Campaign",
      value: campDisplay,
      onValueClick: createClickHandler(
        hasCampaign,
        handlers?.onCampaignClick,
        data?.campaign
      ),
    },
    {
      label: "Prospect",
      value: prsDisplay,
      onValueClick: createClickHandler(
        hasProspect,
        handlers?.onProspectClick,
        data?.prospect
      ),
    },
    {
      label: "Prospect Contact",
      value: prsContactDisplay,
      onValueClick: createClickHandler(
        hasProspectContact,
        handlers?.onProspectContactClick,
        data?.prospectContact
      ),
    },
  ];

  if (hasInquiry) {
    fullWidth.push({
      label: "Inquiry",
      value: inqDisplay,
      onValueClick: createClickHandler(
        hasInquiry,
        handlers?.onInquiryClick,
        data?.inquiry
      ),
    });
  }

  fullWidth.push(
    { label: "Account", value: accountDisplay },
    { label: "Contact", value: contactDisplay }
  );

  return {
    left: [],
    right: [],
    fullWidth,
  };
};
